/**
 * @fileoverview Color Swatch Component
 *
 * Purpose: Display a single color with its CSS variable name, HSL values, and HEX codes
 * Used by: Component library color showcase
 */

import { cn } from "@/utils/misc";

interface ColorSwatchProps {
  name: string;
  variable: string;
  lightValue: string;
  darkValue: string;
  className?: string;
}

/**
 * Convert HSL string (format: "240 5.9% 10%") to HEX color
 */
function hslToHex(hsl: string): string {
  const [h, s, l] = hsl
    .split(" ")
    .map((val) => parseFloat(val.replace("%", "")));

  const hDecimal = l / 100;
  const a = (s * Math.min(hDecimal, 1 - hDecimal)) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = hDecimal - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
}

export function ColorSwatch({
  name,
  variable,
  lightValue,
  darkValue,
  className,
}: ColorSwatchProps) {
  const lightHex = hslToHex(lightValue);
  const darkHex = hslToHex(darkValue);

  return (
    <div className="flex flex-col gap-2">
      {/* Color Preview */}
      <div
        className={cn(
          "h-24 rounded-lg border shadow-sm transition-all hover:shadow-md",
          className,
        )}
      />

      {/* Color Info */}
      <div className="space-y-1">
        <div className="text-sm font-medium">{name}</div>
        <div className="font-mono text-xs text-muted-foreground">
          {variable}
        </div>

        {/* Light Mode */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs">
            <span className="w-10 text-muted-foreground">Light:</span>
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
              {lightValue}
            </code>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="w-10 text-muted-foreground"></span>
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] font-semibold">
              {lightHex}
            </code>
          </div>
        </div>

        {/* Dark Mode */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs">
            <span className="w-10 text-muted-foreground">Dark:</span>
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
              {darkValue}
            </code>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="w-10 text-muted-foreground"></span>
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] font-semibold">
              {darkHex}
            </code>
          </div>
        </div>
      </div>
    </div>
  );
}
